/**
 * Best-effort wrapper around localStorage writes.
 *
 * Safari — and especially Safari Private Browsing, where the quota is effectively
 * zero — throws QuotaExceededError when storage is full or unavailable. An uncaught
 * throw from a write that runs at app bootstrap (the session snapshot) blanks the
 * whole SPA ("white screen after sign-in"); a throw on sign-in / impersonation breaks
 * those flows. Callers must degrade gracefully instead, so every flipto.session write
 * goes through here.
 *
 * Catches any throw from setItem — QuotaExceededError (full / Safari Private Browsing,
 * where the quota is ~0) and also SecurityError (storage blocked by browser policy).
 * Returns true when the value was persisted, false when storage rejected it. There is
 * no client telemetry, so it logs a warning to make field failures diagnosable from the
 * browser console.
 */
export function safeLocalStorageSet(key: string, value: string): boolean {
    try {
        localStorage.setItem(key, value);
        return true;
    } catch (error) {
        // Storage full or unavailable (e.g. Safari Private Browsing). The session is
        // re-injected by the server on every authenticated load, so persistence here
        // is best-effort — losing it must never crash the caller.
        console.warn(`safeLocalStorageSet: could not persist "${key}": ${error instanceof Error ? `${error.name}: ${error.message}` : String(error)}`);
        return false;
    }
}
